import { useGame } from "../em2c-store";

// Debug-only safety layer. It does not modify normal production/economy rules.
// When enabled, any defeat state is immediately recovered without restarting:
// - defeat is cleared
// - life-support inventories refill to their CURRENT storage maxima
// - colonists are revived so an "all colonists lost" defeat can continue

export const GOD_MODE_LIFE_SUPPORT_KEYS = [
  "power",
  "water",
  "oxygen",
  "food",
  "raw_water",
  "potable_water",
  "processed_food",
];

let installed = false;
let rescueInProgress = false;
let unsubscribe = null;

function maxOne(resources, key) {
  const current = resources?.[key];
  if (!current) return resources;
  const max = Number(current.max || 0);
  return {
    ...resources,
    [key]: {
      ...current,
      stored: max,
    },
  };
}

export function maxLifeSupportResources(resources) {
  let next = { ...(resources || {}) };
  for (const key of GOD_MODE_LIFE_SUPPORT_KEYS) {
    if (!next[key]) continue;
    next = maxOne(next, key);
  }
  return next;
}

export function reviveColonists(colonists) {
  return (colonists || []).map((colonist) => ({
    ...colonist,
    health: 1,
    morale: Math.max(Number(colonist.morale ?? 0.8), 0.8),
    hunger: 0,
  }));
}

function rescueFromDefeat(reason) {
  if (rescueInProgress) return;
  rescueInProgress = true;
  try {
    const state = useGame.getState();
    useGame.setState({
      resources: maxLifeSupportResources(state.resources),
      colonists: reviveColonists(state.colonists),
      defeat: false,
      defeatReason: null,
    });

    useGame.getState().pushNotification?.({
      level: "critical",
      text: `GOD MODE RECOVERY: ${reason || "Colony failure"} Life support restored to maximum. Simulation continued.`,
    });
  } finally {
    rescueInProgress = false;
  }
}

export function installDebugGodModeIntegration() {
  if (installed) return;
  installed = true;

  const initial = useGame.getState();
  useGame.setState({
    godMode: Boolean(initial.godMode),

    setGodMode: (enabled) => {
      const on = Boolean(enabled);
      const state = useGame.getState();
      const wasDefeated = Boolean(state.defeat);

      useGame.setState({
        godMode: on,
        resources: on && wasDefeated ? maxLifeSupportResources(state.resources) : state.resources,
        colonists: on && wasDefeated ? reviveColonists(state.colonists) : state.colonists,
        defeat: on ? false : state.defeat,
        defeatReason: on ? null : state.defeatReason,
      });

      useGame.getState().pushNotification?.({
        level: "info",
        text: on
          ? "Debug GOD MODE enabled — defeat protection active"
          : "Debug GOD MODE disabled",
      });
    },

    toggleGodMode: () => {
      const state = useGame.getState();
      state.setGodMode?.(!state.godMode);
    },

    devMaxResource: (key) => {
      const state = useGame.getState();
      const resource = state.resources?.[key];
      if (!resource) return;
      const max = Number(resource.max || 0);
      useGame.setState({
        resources: {
          ...state.resources,
          [key]: { ...resource, stored: max },
        },
      });
      useGame.getState().pushNotification?.({
        level: "info",
        text: `Debug: ${key} filled to ${max}/${max}`,
      });
    },

    devMaxAllResources: () => {
      const state = useGame.getState();
      const resources = { ...state.resources };
      for (const [key, resource] of Object.entries(resources)) {
        resources[key] = { ...resource, stored: Number(resource.max || 0) };
      }
      useGame.setState({ resources });
      useGame.getState().pushNotification?.({
        level: "info",
        text: "Debug: all resources filled to current storage limits",
      });
    },
  });

  // Zustand subscriptions run synchronously. If any current or future system
  // sets defeat=true while God Mode is enabled, this clears the defeat before
  // the next simulation frame and restores survival resources automatically.
  unsubscribe = useGame.subscribe((state, previous) => {
    if (rescueInProgress) return;
    if (!state.godMode || !state.defeat) return;
    if (previous?.defeat === state.defeat && previous?.defeatReason === state.defeatReason) return;
    rescueFromDefeat(state.defeatReason);
  });
}

export function uninstallDebugGodModeIntegration() {
  unsubscribe?.();
  unsubscribe = null;
  installed = false;
}

installDebugGodModeIntegration();
